import { useState } from "react";
import { Button, Form, Input, Modal, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ListPage } from "../../components/ListPage";
import { DocumentStatusTag } from "../../components/DocumentStatusTag";
import { useApi } from "../../helpers/useApi";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { Vehicle } from "../../api-models";

const STATUSES = ["ACTIVE", "MAINTENANCE", "INACTIVE"];

export default function VehiclesPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "Vehicles.Write");
  const { request } = useApi();
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [reloadKey, setReloadKey] = useState(0);

  function openEdit(v: Vehicle) {
    setEditing(v);
    form.setFieldsValue(v);
    setOpen(true);
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) await request({ method: "PUT", url: `/vehicles/${editing.id}`, data: values });
    else await request({ method: "POST", url: "/vehicles", data: values });
    setOpen(false);
    setReloadKey((k) => k + 1);
  }

  return (
    <>
      <ListPage<Vehicle>
        key={reloadKey}
        title="Vehicles"
        endpoint="/vehicles"
        searchPlaceholder="Search vehicles..."
        actions={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                form.resetFields();
                setOpen(true);
              }}
            >
              New vehicle
            </Button>
          )
        }
        columns={[
          { title: "Registration No", dataIndex: "registrationNo", sorter: true },
          { title: "Type", dataIndex: "vehicleType", sorter: true },
          { title: "Capacity", dataIndex: "capacity", sorter: true },
          { title: "Status", dataIndex: "status", sorter: true, render: (v) => <DocumentStatusTag status={v} /> },
          ...(canWrite
            ? [{ title: "", key: "edit", render: (_: unknown, r: Vehicle) => <Button type="link" onClick={() => openEdit(r)}>Edit</Button> }]
            : []),
        ]}
      />
      <Modal title={editing ? "Edit vehicle" : "New vehicle"} open={open} onCancel={() => setOpen(false)} onOk={handleSubmit} destroyOnHidden>
        <Form form={form} layout="vertical">
          <Form.Item name="registrationNo" label="Registration no" rules={[{ required: true }]}>
            <Input disabled={!!editing} />
          </Form.Item>
          <Form.Item name="vehicleType" label="Vehicle type">
            <Input />
          </Form.Item>
          <Form.Item name="capacity" label="Capacity">
            <Input />
          </Form.Item>
          {editing && (
            <Form.Item name="status" label="Status" initialValue="ACTIVE">
              <Select options={STATUSES.map((s) => ({ value: s, label: s }))} />
            </Form.Item>
          )}
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
